import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Sparkles, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCredits } from "@/lib/CreditsContext";
import { Button } from "@/components/ui/button";

const MAX_ATTEMPTS = 10;
const POLL_INTERVAL = 2000;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id");
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [detail, setDetail] = useState(null);
  const { refresh } = useCredits();
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!orderId) {
      setState("error");
      setDetail({ message: "Missing order_id in URL." });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (cancelled) return;
        setDetail(data);
        if (data.payment_status === "paid") {
          setState("success");
          refresh();
          return;
        }
        if (data.payment_status === "failed") {
          setState("error");
          setDetail({ message: "Payment failed. No credits were charged." });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (cancelled) return;
        setState("error");
        setDetail({ message: e?.response?.data?.detail || "Could not check payment status." });
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [orderId, refresh]);

  return (
    <div
      className="min-h-screen bg-[#020617] text-slate-50 terminal-grain flex items-center justify-center p-6"
      data-testid="payment-return"
    >
      <div className="max-w-md w-full bg-slate-900/70 border border-slate-800 rounded-sm p-8">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="h-7 w-7 grid place-items-center bg-cyan-500 text-slate-950 rounded-sm">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.5} />
          </div>
          <span className="font-display font-bold text-base tracking-tight">
            3C<span className="text-cyan-400"> AI</span>
          </span>
        </div>

        {state === "checking" && (
          <div className="text-center py-6" data-testid="payment-checking">
            <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold">Verifying your payment…</h2>
            <p className="text-sm text-slate-400 mt-2">
              Confirming your payment with Razorpay. This usually takes a few seconds.
            </p>
          </div>
        )}

        {state === "success" && (
          <div className="text-center py-4" data-testid="payment-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold">Payment confirmed</h2>
            <p className="text-sm text-slate-400 mt-2">
              <span className="text-cyan-300 font-mono">
                +{detail?.credits_added || 0} credits
              </span>{" "}
              added to your workspace.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 bg-slate-950 border border-slate-800 rounded-sm px-4 py-2 text-sm">
              <span className="text-slate-500 font-mono uppercase tracking-[0.2em] text-[10px]">
                Balance
              </span>
              <span className="font-display font-bold text-lg text-white">
                {detail?.credits_balance ?? "—"}
              </span>
              <span className="text-slate-500 text-xs">credits</span>
            </div>
            <div className="mt-6 flex flex-col gap-2">
              <Link to="/">
                <Button
                  data-testid="back-to-app-btn"
                  className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
                >
                  Back to 3C AI <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
                </Button>
              </Link>
              <Link to="/account">
                <Button
                  variant="outline"
                  className="w-full bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
                >
                  View payment history
                </Button>
              </Link>
            </div>
          </div>
        )}

        {state === "pending" && (
          <div className="text-center py-4" data-testid="payment-pending">
            <Loader2 className="h-8 w-8 text-amber-400 animate-spin mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold">Still processing</h2>
            <p className="text-sm text-slate-400 mt-2">
              Razorpay is taking a moment. Your credits will appear as soon as the
              payment settles — check back in a minute.
            </p>
            <Link to="/">
              <Button
                variant="outline"
                className="mt-6 w-full bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
              >
                Back to app
              </Button>
            </Link>
          </div>
        )}

        {state === "error" && (
          <div className="text-center py-4" data-testid="payment-error">
            <AlertTriangle className="h-10 w-10 text-rose-400 mx-auto mb-4" />
            <h2 className="font-display text-xl font-semibold">Something went wrong</h2>
            <p className="text-sm text-slate-400 mt-2">
              {detail?.message || "We couldn't verify the payment. No credits were charged."}
            </p>
            <Link to="/">
              <Button
                variant="outline"
                className="mt-6 w-full bg-transparent border-slate-700 text-slate-100 hover:bg-slate-900 rounded-sm"
              >
                Back to app
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
